"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupServices = exports.startGuideFromRequest = exports.applyDefaults = void 0;
const guide_registry_1 = require("../services/guide-registry");
const uuid_1 = require("uuid");
const messaging_common_1 = require("@guided-methods/messaging-common");
const chunk_host_1 = require("@guided-methods/chunk-host");
const services_1 = require("../services");
const setup_runner_status_channels_1 = require("./setup-runner-status-channels");
const models_1 = require("../models");
const controllers_1 = require("../controllers");
const logging_service_1 = require("../services/logging/logging-service");
const log_filter_1 = require("./log-filter");
const preconditions_evaluator_service_1 = require("../services/preconditions-evaluator-service");
const url_helpers_1 = require("../utils/url-helpers");
const env_resolver_1 = require("../services/env-resolver");
const defaultOptions = {
    chunkControlKeepAliveTimeoutMs: 62000,
    chunkStatusKeepAliveTimeoutMs: 62000,
    chunkLoadTimeoutMs: 61000,
    chunkStopTimeoutMs: 10000,
    guideControlKeepAliveTimeoutMs: 60000,
    discoverTimeoutMs: 10000,
    guideSendUpstreamKeepAliveIntervalMs: 5000,
};
const applyDefaults = (opts) => ({
    ...defaultOptions,
    ...opts,
});
exports.applyDefaults = applyDefaults;
const startGuideFromRequest = (requestId, hostOptions, hostServices) => {
    let { router, logger, messageService, channels, http, rootChannelId, preconditionsEvaluator } = hostServices;
    let { guideRequestServiceUrl } = hostOptions;
    (0, services_1.readGuideRequest)({
        router,
        logger,
        messageService,
        channels,
        http,
        preconditionsEvaluator
    }, { rootChannelId, guideRequestServiceUrl }, requestId);
};
exports.startGuideFromRequest = startGuideFromRequest;
const handleSubGuideStartRequests = (hostOptions, hostServices, logTemplate) => {
    let { router, logger, messageService, channels } = hostServices;
    const startSuffix = 'start-sub-guide';
    router.on((m) => m.to.endsWith(startSuffix), async (m, t) => {
        if (m.type === messaging_common_1.GuideMessageType.Data && (!m.context?.rootExecutionId)) {
            const message = 'Received start-sub-guide without executionId of a parent. Aborting';
            logger.error({ ...logTemplate, message, data: m.data });
            await messageService.send(channels.browserRunnerStatusChannel, messaging_common_1.GuideMessageType.Data, {}, {
                type: models_1.BrowserRunnerStatusMessageType.Error, message
            });
            return;
        }
        switch (m.type) {
            case messaging_common_1.GuideMessageType.KeepAlive:
                break;
            case messaging_common_1.GuideMessageType.Data:
                logger.info({
                    ...logTemplate,
                    message: `Starting sub-guide with request ${m.data.requestId}`,
                    data: m.data,
                });
                (0, exports.startGuideFromRequest)(m.data.requestId, hostOptions, hostServices);
            case messaging_common_1.GuideMessageType.EndChannel:
                await messageService.send(channels.browserRunnerStatusChannel, messaging_common_1.GuideMessageType.Data, {}, {
                    type: models_1.BrowserRunnerStatusMessageType.GuideCompleted,
                });
        }
    });
};
const handleRootGuideStartRequests = (hostOptions, hostServices, logTemplate) => {
    let { router, logger, messageService, channels } = hostServices;
    const startSuffix = 'start-root-guide';
    router.on((m) => m.to.includes("start-root-guide"), async (m, t) => {
        if (m.type === messaging_common_1.GuideMessageType.Data && (m.context.executionId || m.context.requestId)) {
            const message = 'Received start-root-guide request with executionId or requestId of a parent. Aborting';
            logger.error({ ...logTemplate, message, data: m.data });
            await messageService.send(channels.browserRunnerStatusChannel, messaging_common_1.GuideMessageType.Data, {}, {
                type: models_1.BrowserRunnerStatusMessageType.Error, message
            });
            return;
        }
        switch (m.type) {
            case messaging_common_1.GuideMessageType.KeepAlive:
                break;
            case messaging_common_1.GuideMessageType.Data:
                logger.info({ ...logTemplate, message: `Starting root guide with request ${m.data.requestId}`, data: m.data });
                (0, exports.startGuideFromRequest)(m.data.requestId, hostOptions, hostServices);
            case messaging_common_1.GuideMessageType.EndChannel:
                await messageService.send(channels.browserRunnerStatusChannel, messaging_common_1.GuideMessageType.Data, {}, {
                    type: models_1.BrowserRunnerStatusMessageType.GuideCompleted,
                });
        }
    });
};
const setupServices = async (orgOptions) => {
    const updateOptions = (input) => {
        const resolveTestMode = (opt) => !opt.runnerId || opt.runnerId.length === 0;
        const resolveRunnerId = (opt) => (opt.runnerId && opt.runnerId.length > 0) ? opt.runnerId : (0, uuid_1.v4)();
        const resolveRootChannelId = (runnerId) => (0, chunk_host_1.makeRunnerRoot)((runnerId && runnerId.length > 0) ? runnerId : (0, uuid_1.v4)(), true);
        const resolveGuideControlKeepAliveTimeoutMs = (opt) => resolveTestMode(opt) ? 10000 : defaultOptions.guideControlKeepAliveTimeoutMs;
        const testMode = resolveTestMode(input);
        const runnerId = resolveRunnerId(input);
        const rootChannelId = resolveRootChannelId(runnerId);
        const guideControlKeepAliveTimeoutMs = resolveGuideControlKeepAliveTimeoutMs(input);
        return { ...input, runnerId, rootChannelId, guideControlKeepAliveTimeoutMs };
    };
    const options = updateOptions(orgOptions);
    const { appName, appVersion, runnerId, rootChannelId, signalrHubUrl, tokenService, messageUrl, router, registryUrl, wcsUrl, storageUrl, loggingServiceUrl } = options;
    const { showLocalLogs, showVerboseLogging, showXstateLayerLogging } = (0, url_helpers_1.getQueryParameters)();
    const localLoggingSelected = showLocalLogs === true;
    let selectedLogLevel = log_filter_1.LogFilterLevel.Verbose;
    const logConditional = (transport) => (0, messaging_common_1.logWhen)(transport, (0, log_filter_1.getLogFilter)(selectedLogLevel));
    const consoleLogger = logConditional((0, services_1.createBrowserConsoleLogger)({ showLocalLogs, showVerboseLogging, showXstateLayerLogging }));
    const logTemplate = {
        rootChannelId,
        runnerId,
        mode: messaging_common_1.LogMode.Framework,
        layer: 'HostSetup',
    };
    const loggingService = logConditional((0, logging_service_1.createLogger)(tokenService, appName, appVersion, runnerId, loggingServiceUrl, (0, env_resolver_1.getEnvironment)()));
    const logger = !localLoggingSelected
        ? new messaging_common_1.Logger(loggingService)
        : new messaging_common_1.Logger((0, messaging_common_1.combineLogOutput)(consoleLogger, loggingService));
    logger.debug({ ...logTemplate, message: "Guide host app logging set up" });
    try {
        const http = new services_1.AxiosHttpService({}, logger, tokenService);
        const chunkHostServices = (0, chunk_host_1.setupChunkHost)({ ...options, logger, uuid: uuid_1.v4, rootChannelId });
        const { messageService } = chunkHostServices;
        const guideRegistry = (0, guide_registry_1.createGuideRegistry)(http, registryUrl);
        const browserHostChannels = (0, setup_runner_status_channels_1.setupRunnerStatusChannels)(rootChannelId);
        const preconditionsEvaluator = (0, preconditions_evaluator_service_1.setupPreconditionsEvaluator)({ messageService, channels: browserHostChannels, logger, router }, { retryTimeout: -1, rootChannelId });
        const guideHostServices = (0, chunk_host_1.setupGuideHost)({
            ...options,
            logger,
            guideRegistry,
            uuid: uuid_1.v4,
            messageService,
            rootChannelId,
        });
        const channels = {
            ...chunkHostServices.channels,
            ...guideHostServices.channels,
            ...browserHostChannels,
        };
        const forwardIncomingMessage = (message) => {
            if (message.type === messaging_common_1.GuideMessageType.Data && message.context.executionId && message.context.lastError) {
                guideHostServices.guideMonitor.registerLastError(message.context.executionId, message.context.lastError);
            }
            messageService.forward(message);
        };
        const pollForMassages = async (pollLatestMs) => {
            try {
                logger.info({ ...logTemplate, message: 'Polling for messages' });
                const response = await http.put(`${(0, env_resolver_1.getMessageForwarderUrlByEnv)()}api/v1/messages`, { groupId: rootChannelId, timestamp: Date.now() - pollLatestMs });
                if (response?.data) {
                    const messages = response?.data?.map(o => JSON.parse(o.context));
                    logger.debug({ ...logTemplate, message: 'Forwarding polled messages', data: messages });
                    messages?.map((m) => forwardIncomingMessage({ ...m, context: { ...m.context, mechanism: 'polling' } }));
                }
            }
            catch (error) {
                logger.error({ ...logTemplate, message: `Error when trying to poll for messages: ${error instanceof Error ? error.message : "Unknown error"}` });
            }
        };
        let signalRConnected = false;
        let pollingRunning = false;
        const signalRService = new services_1.SignalrService(logger, tokenService, signalrHubUrl, async (reconnecting) => {
            logger.info({
                ...logTemplate,
                message: 'Connected to SignalR hub',
                layer: 'SignalR',
            });
            if (reconnecting) {
                logger.debug({
                    ...logTemplate,
                    message: 'Reconnecting SignalR',
                    layer: 'SignalR',
                });
                signalRService.unsubscribe(rootChannelId);
            }
            signalRService.onGuideMessage((m) => forwardIncomingMessage({ ...m, context: { ...m.context, mechanism: 'signalR' } }));
            signalRService.onStreamIoValues((ioData) => streamingService.processSignalRMessage(ioData));
            await signalRService.subscribe(rootChannelId);
            if (!signalRConnected) {
                messageService.send(channels.browserRunnerStatusChannel, messaging_common_1.GuideMessageType.Data, {}, {
                    type: models_1.BrowserRunnerStatusMessageType.SignalRConnected,
                });
                signalRConnected = true;
            }
            else {
                logger.info({
                    ...logTemplate,
                    message: 'Already connected to SignalR',
                    layer: 'SignalR',
                });
            }
        }, () => {
            if (pollingRunning)
                return;
            pollingRunning = true;
            console.warn('Switching to polling');
            logger.info({ ...logTemplate, message: 'Setting up polling interval' });
            const interval = setInterval(async () => {
                if (!signalRService.connected) {
                    await pollForMassages(2500);
                }
                else {
                    console.warn('Switching away from polling');
                    logger.info({ ...logTemplate, message: 'Stopping polling interval' });
                    clearInterval(interval);
                    pollingRunning = false;
                    await pollForMassages(2500);
                }
            }, 2000);
        });
        const streamingService = new services_1.StreamingService(http, signalRService, wcsUrl, messageService, logger);
        await signalRService.connect().catch((e) => {
            console.error('signalR error');
            logger.error({ ...logTemplate, message: 'Failed to connect to SignalR' });
            messageService.send(channels.browserRunnerStatusChannel, messaging_common_1.GuideMessageType.Data, {}, {
                type: models_1.BrowserRunnerStatusMessageType.Error,
                message: 'Failed to connect to SignalR - Upstream communication not established',
            });
        });
        const defController = (0, controllers_1.defaultController)(messageUrl, http, logger, rootChannelId, streamingService);
        const guideStorage = (0, services_1.createGuideStorage)(http, storageUrl);
        const hostServices = {
            ...chunkHostServices,
            ...guideHostServices,
            rootChannelId,
            router,
            logger,
            channels,
            http,
            tokenService,
            defaultController: defController,
            guideStorage,
            preconditionsEvaluator,
            guideDestroyers: [],
        };
        handleRootGuideStartRequests(options, hostServices, logTemplate);
        handleSubGuideStartRequests(options, hostServices, logTemplate);
        return { hostServices, logger };
    }
    catch (e) {
        logger.error({ ...logTemplate, message: `Failed to setup services: ${e instanceof Error ? e.message : 'Unknown error'}` });
        return { logger };
    }
};
exports.setupServices = setupServices;
